import { Box, Grid, Skeleton, Stack, Typography } from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import React, { useEffect, useState } from 'react';
import { MeetingDto, MyProxyGivenToDto } from '../../../services/dtos/meeting.dto';
import StyledTable from '../../commun/table';
import NoData from '../../commun/no-data';
import { HeadCellDto } from '../../../services/dtos/head-cell.dto';
import GetFiles from '../../action/get-files';
import { ResultSearchDto } from '../../../services/dtos/result-search.dto';
import MeetingForm from '../../forms/meeting/meeting.form';
import { MeetingFormDto } from '../../../services/dtos/meeting-form.dto';
import { getMeetings } from '../../../services/general-meeting.service';
import { getValueLocalStorage } from '../../../hooks/use-local-storage.hook';
import { showAxiosMessageError } from '../../../utils/error-message.util';
import { useSnackbars } from '../../../hooks/snackbar.hook';
import { MandateGeneralDto } from '../../../services/dtos/mandate-general.dto';
import { MandateEstablishmentDto } from '../../../services/dtos/mandate-establishment-dto';
import { MandateTerritoryDto } from '../../../services/dtos/mandate-territory.dto';
import { CommissionDto } from '../../../services/dtos/commission.dto';
import MeetingAttendance from '../../action/meeting-attendance';

const DEFAULT_HEAD_CELLS: HeadCellDto<MeetingDto, keyof MeetingDto>[] = [
    { id: 'date', label: 'Date', sort: false, width: 10 },
    { id: 'startTime', label: 'Heure Début', sort: false, width: 10 },
    { id: 'duration', label: 'Durée', sort: false, width: 10 },
    { id: 'location', label: 'Lieu', sort: false, width: 40 },
    { id: 'status', label: 'Etat', sort: false, width: 20 },
];

/**
 * Ui pour la page d'assemblée générale
 * @constructor
 */
export default function MeetingUi(
    props: Readonly<{
        mandate?: MandateGeneralDto | MandateEstablishmentDto | MandateTerritoryDto;
        handlerGetMeetingFiles: (meeting: MeetingDto) => void;
        commission?: CommissionDto;
        handlerUpdateMeetingAttendance: (meeting: MeetingDto, attendance: boolean) => Promise<MeetingDto>;
    }>
) {
    const mandateId = props.mandate?.id;
    const commissionId = props.commission?.id;
    // Données de l'utilisateur nécessaires pour la page en cours
    const [meetingsPassed, setMeetingsPassed] = useState<ResultSearchDto<MeetingDto>>();
    // Données de l'utilisateur nécessaires pour la page en cours
    const [meetingsUpcoming, setMeetingsUpcoming] = useState<ResultSearchDto<MeetingDto>>();
    // Loader
    const [loading, setLoading] = useState<boolean>(false);
    // Loader
    const [loadingFutur, setLoadingFutur] = useState<boolean>(false);
    // SnackBar pour les messages
    const showMessage = useSnackbars();
    // Page affichée premier tableau
    const [pageFutur, setPageFutur] = useState(0);
    // Page affichée deuxieme tableau
    const [page, setPage] = useState(0);
    // items par page premier tableau
    const [itemsPerPageFutur, setItemsPerPageFutur] = useState(10);
    // donnees du formulaire
    const [form, setForm] = useState<MeetingFormDto>();
    // donnees du formulaire tableau à venir
    const [formFutur, setFormFutur] = useState<MeetingFormDto>();
    // items par page deuxieme tableau
    const [itemsPerPage, setItemsPerPage] = useState(10);
    // desc asc
    const [order, setOrder] = React.useState<'asc' | 'desc'>();
    // sort
    const [sort, setSort] = React.useState<keyof MeetingDto>();
    // desc asc pour futur
    const [orderFutur, setOrderFutur] = React.useState<'asc' | 'desc'>();
    // sort pour futur
    const [sortFutur, setSortFutur] = React.useState<keyof MeetingDto>();
    // Entete des cellules des tableaux
    const [headCells, setHeadCells] = useState<HeadCellDto<MeetingDto, keyof MeetingDto>[]>(DEFAULT_HEAD_CELLS);

    const [meetingAttendanceLoadingId, setMeetingAttendanceLoadingId] = useState<string>();

    useEffect(() => {
        const currentUser = getValueLocalStorage('userAccount');
        if (!mandateId || currentUser === null || currentUser === '') {
            return;
        }

        setLoading(true);
        setLoadingFutur(true);
        setHeadCells(DEFAULT_HEAD_CELLS);

        getMeetings(mandateId, 'Passed', 0, 10, undefined, undefined, {
            commission: commissionId,
        })
            .then(result => {
                setMeetingsPassed(result);
            })
            .catch(error => {
                console.error('Erreur lors de la récupération des réunions');
                showAxiosMessageError(error, showMessage);
            })
            .finally(() => {
                setLoading(false);
            });

        getMeetings(mandateId, 'Upcoming', 0, 10, undefined, undefined, {
            commission: commissionId,
        })
            .then(result => {
                setMeetingsUpcoming(result);
            })
            .catch(error => {
                console.error('Erreur lors de la récupération des réunions');
                showAxiosMessageError(error, showMessage);
            })
            .finally(() => {
                setLoadingFutur(false);
            });
    }, [commissionId, mandateId, showMessage]);

    const updateMeetingInResults = (meetingToUpdate: MeetingDto) => {
        setMeetingsUpcoming(currentValue =>
            currentValue
                ? {
                      ...currentValue,
                      items: currentValue.items.map(item => (item.id === meetingToUpdate.id ? meetingToUpdate : item)),
                  }
                : currentValue
        );
        setMeetingsPassed(currentValue =>
            currentValue
                ? {
                      ...currentValue,
                      items: currentValue.items.map(item => (item.id === meetingToUpdate.id ? meetingToUpdate : item)),
                  }
                : currentValue
        );
    };

    const handleUpdateMeetingAttendance = async (meeting: MeetingDto, attendance: boolean) => {
        setMeetingAttendanceLoadingId(meeting.id);

        try {
            const updatedMeeting = await props.handlerUpdateMeetingAttendance(meeting, attendance);
            updateMeetingInResults(updatedMeeting);
        } finally {
            setMeetingAttendanceLoadingId(undefined);
        }
    };

    const handleProxyChange = (meeting: MeetingDto, myProxyGivenTo: MyProxyGivenToDto | null) => {
        updateMeetingInResults({ ...meeting, myProxyGivenTo });
    };

    //Action à mettre dans le tableau
    const action = (meeting: MeetingDto) => (
        <Stack spacing={1} justifyContent="center" alignItems="center">
            <Box>
                <GetFiles handlerGetMeetingFiles={props.handlerGetMeetingFiles} meeting={meeting} />
            </Box>
            <MeetingAttendance
                compact
                meeting={meeting}
                loading={meetingAttendanceLoadingId === meeting.id}
                onChange={attendance => handleUpdateMeetingAttendance(meeting, attendance)}
                onProxyChange={myProxyGivenTo => handleProxyChange(meeting, myProxyGivenTo)}
            />
        </Stack>
    );

    const handlerGetMeeting = (
        status: string,
        pageToTable: number,
        itemsPerPageToTable: number,
        orderParam?: string,
        sortParam?: string,
        paramForm?: MeetingFormDto,
        commissionForMeetings?: CommissionDto
    ) => {
        if (props.mandate) {
            paramForm = {
                ...paramForm,
                commission: commissionForMeetings?.id,
            };
            //Récupération de l'utilisateur courant
            getMeetings(props.mandate.id, status, pageToTable, itemsPerPageToTable, orderParam, sortParam, paramForm)
                .then(result => {
                    if (status === 'Passed') {
                        setMeetingsPassed(result);
                    } else {
                        setMeetingsUpcoming(result);
                    }
                })
                .catch(error => {
                    console.error('Erreur lors de la récupération des réunions');
                    showAxiosMessageError(error, showMessage);
                })
                .finally(() => {
                    setLoading(false);
                    setLoadingFutur(false);
                });
        }
    };
    const handlerSearch = (paramForm: MeetingFormDto) => {
        setLoading(true);
        setPage(0);
        setForm(paramForm);
        handlerGetMeeting('Passed', 0, itemsPerPage, order, sort, paramForm, props.commission);
    };

    const handlerSearchFutur = (paramForm: MeetingFormDto) => {
        setLoadingFutur(true);
        setPageFutur(0);
        setFormFutur(paramForm);
        handlerGetMeeting('Upcoming', 0, itemsPerPageFutur, orderFutur, sortFutur, paramForm, props.commission);
    };

    //Méthode au changement de la page
    const handleChangePageFutur = (_event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
        setLoadingFutur(true);
        setPageFutur(newPage);
        handlerGetMeeting('Upcoming', newPage, itemsPerPageFutur, orderFutur, sortFutur, formFutur, props.commission);
    };

    //Méthode au changement de la page
    const handleChangePage = (_event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
        setLoading(true);
        setPage(newPage);
        handlerGetMeeting('Passed', newPage, itemsPerPage, order, sort, form, props.commission);
    };

    //Méthode au changement d'items par page
    const handleChangeItemsPerPageFutur = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const nextItemsPerPage = Number.parseInt(event.target.value, 10);
        setLoadingFutur(true);
        setItemsPerPageFutur(nextItemsPerPage);
        setPageFutur(0);
        handlerGetMeeting('Upcoming', 0, nextItemsPerPage, orderFutur, sortFutur, formFutur, props.commission);
    };

    //Méthode au changement d'items par page
    const handleChangeItemsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const nextItemsPerPage = Number.parseInt(event.target.value, 10);
        setLoading(true);
        setItemsPerPage(nextItemsPerPage);
        setPage(0);
        handlerGetMeeting('Passed', 0, nextItemsPerPage, order, sort, form, props.commission);
    };

    //handler sort
    const handlerSortBy = (orderParam: 'asc' | 'desc', orderByParam: keyof MeetingDto) => {
        setOrder(orderParam);
        setSort(orderByParam);
        handlerGetMeeting('Passed', page, itemsPerPage, orderParam, orderByParam, form, props.commission);
    };

    //handler sort pour le tableau futur
    const handlerSortByForFutur = (orderParam: 'asc' | 'desc', orderByParam: keyof MeetingDto) => {
        setOrderFutur(orderParam);
        setSortFutur(orderByParam);
        handlerGetMeeting('Upcoming', pageFutur, itemsPerPageFutur, orderParam, orderByParam, form, props.commission);
    };

    //Mise en place du tableau reunion futur
    const showTableFutur = () => {
        if (meetingsUpcoming === undefined) {
            return <Skeleton variant="rectangular" height={250}></Skeleton>;
        }
        if (meetingsUpcoming.totalItems === 0) {
            return <NoData title={'Information : '} content={'Aucune information à venir'} />;
        } else {
            return (
                <StyledTable
                    data={meetingsUpcoming.items}
                    searching={loadingFutur}
                    action={action}
                    headCells={headCells}
                    totalItems={meetingsUpcoming.totalItems}
                    page={pageFutur}
                    handleChangePage={handleChangePageFutur}
                    handleChangeRowsPerPage={handleChangeItemsPerPageFutur}
                    itemsPerPage={itemsPerPageFutur}
                    handlerSortBy={handlerSortByForFutur}
                    order={orderFutur}
                    sort={sortFutur}
                ></StyledTable>
            );
        }
    };

    //Mise en place du tableau reunion passées
    const showTablePassed = () => {
        if (meetingsPassed === undefined) {
            return <Skeleton variant="rectangular" height={250}></Skeleton>;
        }
        if (meetingsPassed.totalItems === 0) {
            return <NoData title={'Information : '} content={'Aucune information à venir'} />;
        } else {
            return (
                <StyledTable
                    data={meetingsPassed.items}
                    searching={loading}
                    action={action}
                    headCells={headCells}
                    totalItems={meetingsPassed.totalItems}
                    page={page}
                    handleChangePage={handleChangePage}
                    handleChangeRowsPerPage={handleChangeItemsPerPage}
                    itemsPerPage={itemsPerPage}
                    handlerSortBy={handlerSortBy}
                    order={order}
                    sort={sort}
                ></StyledTable>
            );
        }
    };

    //Retourne la vue
    return (
        <Grid container className={'tab-content'} rowSpacing={1}>
            <Grid item xs={12}>
                <Typography variant="h3" className={'title'}>
                    <CalendarMonthIcon /> Mes réunions
                </Typography>
            </Grid>

            <Grid item xs={12} md={1} />
            <Grid item xs={12} md={10}>
                <Stack>
                    <Typography variant="h4" className={'title'}>
                        Réunions à venir
                    </Typography>
                    {/* Formulaire sur les réunions terminées */}
                    <MeetingForm searching={loadingFutur} handlerSearch={handlerSearchFutur} idPrefix="upcoming" />
                    {/* On affiche d'abord les réunions à venir */}
                    {showTableFutur()}
                    <Typography variant="h4" className={'title margin-top-50'}>
                        Réunions terminées
                    </Typography>
                    {/* Formulaire sur les réunions terminées */}
                    <MeetingForm searching={loading} handlerSearch={handlerSearch} idPrefix="passed" />
                    {/* On affiche ensuite les réunions terminées */}
                    {showTablePassed()}
                </Stack>
            </Grid>
            <Grid item xs={12} md={1} />
        </Grid>
    );
}
